import { authenticatedApiClient } from '../axios';
import { BUILDING_CONFIG_ENDPOINT, BUILDING_TOOLTIP_ENDPOINT } from '../config/api';
import { OnboardingConfig, Raffle, BuildingConfig, BuildingTooltip } from '@cobu/types';

/**
 * Building service for handling building-related operations
 */
class BuildingService {
  /**
   * Get onboarding configuration for a building
   */
  async getOnboardingConfig(buildingId: string): Promise<OnboardingConfig[]> {
    const config = { params: { building: buildingId } }
    const response = await authenticatedApiClient.get<OnboardingConfig[]>(`/onboardingconfig`, config);
    return response;
  }

  /**
   * Get active raffle for a building
   */
  async getRaffle(buildingId: string): Promise<Raffle[]> {
    const config = { params: { building: buildingId, active: true } }
    const response = await authenticatedApiClient.get<Raffle[]>(`/v2/raffle`, config);
    return response;
  }

  /**
   * Get building tooltip for a building
   */
  async getBuildingTooltip(buildingId: string): Promise<BuildingTooltip[]> {
    const response = await authenticatedApiClient.get<BuildingTooltip[]>(`${BUILDING_TOOLTIP_ENDPOINT}/${buildingId}`);
    return response;
  }

  /**
   * Get building configuration flags for feature toggles
   */
  async getBuildingConfig(versionNumber?: string): Promise<BuildingConfig | null> {
    try {
      // Use VITE_APP_VERSION from .env (backend requires versionNumber)
      const resolvedVersion = versionNumber ?? import.meta.env.VITE_APP_VERSION;
      
      if (!resolvedVersion) {
        console.error('[BuildingService] VITE_APP_VERSION is not defined in .env');
        return null;
      }
      
      const config = { params: { versionNumber: resolvedVersion } };
      const response = await authenticatedApiClient.get<BuildingConfig>(BUILDING_CONFIG_ENDPOINT, config);
      return response;
    } catch {
      return null;
    }
  }

  /**
   * Mark a building survey as completed
   */
  async completeBuildingSurvey(surveyId: string): Promise<void> {
    await authenticatedApiClient.post(`/buildingsurvey/${surveyId}/complete`);
  }

  /**
   * Mark a building tooltip survey as completed
   */
  async completeBuildingTooltipSurvey(surveyId: string): Promise<void> {
    await authenticatedApiClient.post(`/tooltip/${surveyId}/complete`);
  }
}

// Export a singleton instance
const buildingServiceInstance = new BuildingService();
export default buildingServiceInstance; 